import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Card, Button, Spinner, Container, FormLabel } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { PutJson, PostJson, GetJson } from '@awp/helpers/fetchReact';
import { REST_API, FORM_MODE, Views } from '@awp/constants';
import {
  FormInput,
  FormSelect,
  FormCheckbox,
  FormTextArea,
  FormListWithAction,
} from '@awp/components/Common/AwpFormInputs';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import { getAvalancheGeographicAreas, selectGeographicAreaPointsDd } from '@awp/stores/avalancheSlice';
import { Permissions } from '@awp/constants';
import Authorize from '@awp/components/Authorize';
import AwpCloseCancelButtons from '../../components/AwpCloseCancelButton';
import { Crud, getCrud } from '@awp/shared/Crud';

const RoadClosureAreasEdit = ({ setMode, mode, area, setArea, data, setData, areaLines, setAreaLines }) => {
  const defaultDto = {
    program: null,
    area: null,
    name: null,
    fromPoint: null,
    toPoint: null,
  };
  const sortedList = (collection) => {
    return collection?.sort((a, b) => {
      const extractNumber = (name) => {
        const match = name.match(/^(\d+)/);
        return match ? parseInt(match[0], 10) : null;
      };
      const numA = extractNumber(a.name);
      const numB = extractNumber(b.name);
      if (numA === null && numB !== null) {
        return -1;
      }
      if (numA !== null && numB === null) {
        return 1;
      }
      if (numA !== null && numB !== null) {
        return numA - numB;
      }
      return a.name.localeCompare(b.name);
    });
  };
  const [loading, setLoading] = useState(false);
  const [roadClosureAreaData, setRoadClosureAreaData] = useState(mode !== FORM_MODE.CREATE ? { ...data } : defaultDto);
  const [entityErrors, setEntityErrors] = useState(null);
  const [areaId, setAreaId] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const programAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));

  const avalanchePoints = useSelector((state) => selectGeographicAreaPointsDd(state));
  const [areaPoints, setAreaPoints] = useState(avalanchePoints);
  const [isPointsEditable, setPointsEditable] = useState(true);
  const [deactivationStatus, setDeactivationStatus] = useState(false);
  const [activeBtnMsg, setActiveBtvMsg] = useState(null);

  const avalancheList = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));
  const [areaList, setAreaList] = useState(
    mode === FORM_MODE.CREATE ? avalancheList.filter((x) => !x.isDisabled) : avalancheList,
  );

  const [pointList, setPointList] = useState(
    sortedList(
      mode === FORM_MODE.CREATE
        ? areaPoints.filter((x) => !x.isDisabled && x.pointTypeName === 'ROAD CLOSURE POINT')
        : areaPoints.filter((x) => x.pointTypeName === 'ROAD CLOSURE POINT'),
    ),
  );

  const crud = getCrud('Road Closure Area', mode);
  const { readOnly, title } = crud;
  const [pathList, setPathList] = useState([]);

  const avalanchePrograms = mode === FORM_MODE.CREATE ? programAreas.filter((x) => !x.isDisabled) : programAreas;

  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const avalancheProgram = avalanchePrograms.find((x) => x.value === area.parentId);
    if (avalancheProgram) {
      const areaListFilterCriteria =
        mode === FORM_MODE.CREATE
          ? (x) => x.parentId === avalancheProgram.value && !x.isDisabled
          : (x) => x.parentId === avalancheProgram.value;
      setAreaList(avalancheList.filter(areaListFilterCriteria).sort((a, b) => a.name.localeCompare(b.name)));
      const pointListFilterCriteria =
        mode !== FORM_MODE.EDIT
          ? (x) => x.areaId === area.value && !x.isDisabled && x.pointTypeName === 'ROAD CLOSURE POINT'
          : (x) => x.areaId === area.value && x.pointTypeName === 'ROAD CLOSURE POINT';
      setPointList(sortedList(areaPoints.filter(pointListFilterCriteria)));
    }
    if (mode !== FORM_MODE.CREATE) {
      loadFullData();
    }
    if (mode === FORM_MODE.EDIT) {
      loadGeographicLine();
      isGeographicLineEditable();
    }
  }, [mode]);

  const loadFullData = async () => {
    const urlGeographicpointslinebyarea = `${REST_API.NON_SPATIAL_REFERENCE_DATA}geographicpointslinebyarea/${data.value}`;
    GetJson(urlGeographicpointslinebyarea)
      .then((response) => response.json())
      .then(async (records) => {
        if (records) {
          const selectedLocations = records.map((x) => pointList.find((y) => y.value == x.Id));
          const fromPoint = selectedLocations.length && selectedLocations.length > 0 ? selectedLocations[0] : null;
          const toPoint = selectedLocations.length && selectedLocations.length > 1 ? selectedLocations[1] : null;
          setRoadClosureAreaData((prev) => ({
            ...prev,
            fromPoint: fromPoint,
            toPoint: toPoint,
          }));
          canGeographicAreaBeDeactivated(fromPoint?.value, toPoint?.value);
        }
      });
    const urlGeographicpathlinebyarea = `${REST_API.NON_SPATIAL_REFERENCE_DATA}geographicpathlinebyarea/${data.value}`;
    GetJson(urlGeographicpathlinebyarea)
      .then((response) => response.json())
      .then(async (records) => {
        if (records) {
          setPathList(records);
        }
        setLoading(false);
      });
  };

  const canGeographicAreaBeDeactivated = async (pointFromId = null, pointToId = null) => {
    let urlCanDeactivate = `${REST_API.NON_SPATIAL_REFERENCE_DATA}cangeographicareabedeactivated?geoLine=${data.value}`;
    if (pointFromId != null && pointToId != null) {
      urlCanDeactivate += `&pointFrom=${pointFromId}&pointTo=${pointToId}`;
    }

    GetJson(urlCanDeactivate)
      .then((response) => response.json())
      .then((canBeDeactivated) => {
        setDeactivationStatus(canBeDeactivated);
        if (!canBeDeactivated) {
          setActiveBtvMsg(
            'A Road Closure Event is currently in effect for this area; therefore, it cannot be deactivated.',
          );
        }
      });
  };

  const loadGeographicLine = async () => {
    const url = `${REST_API.NON_SPATIAL_REFERENCE_DATA}geographicline/${data.value}`;

    GetJson(url)
      .then((response) => response.json())
      .then(async (record) => {
        if (record) {
          setRoadClosureAreaData((prev) => ({
            ...prev,
            ConcurrencyControlNumber: record.ConcurrencyControlNumber,
          }));
        }
      });
  };

  const isGeographicLineEditable = async () => {
    const url = `${REST_API.NON_SPATIAL_REFERENCE_DATA}isgeographiclineeditable/${data.value}`;

    GetJson(url)
      .then((response) => response.json())
      .then(async (record) => {
        setPointsEditable(record);
      });
  };

  const createRoadClosureAreas = (setSubmitting, resetForm, setErrors, roadClosureAreaDto) => {
    const url = `${REST_API.NON_SPATIAL_REFERENCE_DATA}geographicline`;
    PostJson(url, roadClosureAreaDto)
      .then(async (response) => {
        if (response.ok) {
          const roadClosureArea = await response.json();
          const rcArea = {
            value: roadClosureArea.Id,
            name: roadClosureArea.Name,
            description: roadClosureArea.Description,
            areaId: roadClosureArea.AreaId,
            fromPoint: pointList.find((x) => x.value == roadClosureAreaDto.LocationIdFrom),
            toPoint: pointList.find((x) => x.value == roadClosureAreaDto.LocationIdTo),
            programId: avalanchePrograms.find((x) => x.value === roadClosureArea.AreaId),
            isDisabled: !roadClosureArea.IsActive,
          };
          updateData(rcArea);
          setData(rcArea);
          resetForm({
            values: {
              validateOnMount: true,
              program: avalanchePrograms.find((x) => x.value === area.parentId),
              area: area,
              name: roadClosureArea.Name,
              description: roadClosureArea.Description,
              fromPoint: pointList.find((x) => x.value == roadClosureAreaDto.LocationIdFrom),
              toPoint: pointList.find((x) => x.value == roadClosureAreaDto.LocationIdTo),
              active: roadClosureArea.IsActive,
            },
          });
          toast.success('Road Closure Areas data has been created');
          setMode(FORM_MODE.VIEW);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          resetForm({
            values: {
              validateOnMount: true,
              program: avalanchePrograms.find((x) => x.value === area.parentId),
              area: area,
              name: roadClosureAreaDto.Name,
              description: roadClosureAreaDto.Description,
              fromPoint: pointList.find((x) => x.value == roadClosureAreaDto.LocationIdFrom),
              toPoint: pointList.find((x) => x.value == roadClosureAreaDto.LocationIdTo),
              active: roadClosureAreaDto.IsActive,
            },
          });
          if (errors.entity) setEntityErrors({ ['Road Closure Area']: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const updateRoadClosureAreas = (setSubmitting, resetForm, setErrors, roadClosureAreaDto) => {
    const url = `${REST_API.NON_SPATIAL_REFERENCE_DATA}geographicline/${roadClosureAreaDto.value}`;
    PutJson(url, roadClosureAreaDto)
      .then(async (response) => {
        if (response.ok) {
          const roadClosureArea = await response.json();
          updateData({});
          resetForm({
            values: {
              validateOnMount: true,
              program: avalanchePrograms.find((x) => x.value === area.parentId),
              area: area,
              name: roadClosureArea.Name,
              description: roadClosureArea.Description,
              fromPoint: pointList.find((x) => x.value == roadClosureArea.LocationIdFrom),
              toPoint: pointList.find((x) => x.value == roadClosureArea.LocationIdTo),
              active: roadClosureArea.IsActive,
            },
          });
          toast.success('Road Closure Area data has been updated');
          setMode(FORM_MODE.VIEW);
        } else if (response.status === 412) {
          const errors = await response.json();
          resetForm({
            values: {
              validateOnMount: true,
              program: avalanchePrograms.find((x) => x.value === area.parentId),
              area: area ?? avalancheList.find((x) => x.value === roadClosureAreaDto.AreaId),
              name: roadClosureAreaDto.Name,
              description: roadClosureAreaDto.Description,
              fromPoint: pointList.find((x) => x.value == roadClosureAreaDto.LocationIdFrom),
              toPoint: pointList.find((x) => x.value == roadClosureAreaDto.LocationIdTo),
              active: roadClosureAreaDto.IsActive,
            },
          });
          setErrors(errors);
          if (errors.entity) setEntityErrors({ ['Road Closure Area']: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const updateData = (data) => {
    if (mode !== FORM_MODE.CREATE) {
      const index = areaLines.findIndex((x) => x.value === roadClosureAreaData.value);
      if (index < 0) return;
      const newLine = {
        name: roadClosureAreaData.name,
        value: roadClosureAreaData.value,
        description: roadClosureAreaData.description,
        areaId: areaId ?? area.value,
        programId: roadClosureAreaData.programId ?? avalanchePrograms.find((x) => x.value === (areaId ?? area.value)),
        isDisabled: roadClosureAreaData.isDisabled,
      };
      areaLines[index] = { ...newLine };
      setAreaLines([...areaLines]);
    } else {
      roadClosureAreaData.value = data.value;
      setAreaLines([
        ...areaLines,
        {
          name: data.name,
          value: data.value,
          description: data.description,
          areaId: data.areaId,
          programId: data.programId ?? avalanchePrograms.find((x) => x.value === data.areaId),
          isDisabled: data.isDisabled,
        },
      ]);
    }
  };

  const programOnChange = (opt, setFieldValue) => {
    setFieldValue('area', {}, false);
    area.value = null;
    const areaListFilterCriteria =
      mode === FORM_MODE.CREATE ? (x) => x.parentId === opt.value && !x.isDisabled : (x) => x.parentId === opt.value;
    setAreaList(avalancheList.filter(areaListFilterCriteria).sort((a, b) => a.name.localeCompare(b.name)));
    setFieldValue('fromPoint', {}, false);
    setFieldValue('toPoint', {}, false);
    const pointListFilterCriteria =
      mode === FORM_MODE.CREATE
        ? (x) => x.programId === opt.value && !x.isDisabled && x.pointTypeName === 'ROAD CLOSURE POINT'
        : (x) => x.programId === opt.value && x.pointTypeName === 'ROAD CLOSURE POINT';
    setPointList(sortedList(areaPoints.filter(pointListFilterCriteria)));
  };

  const areaOnChange = (opt, setFieldValue) => {
    setAreaId(opt.value);
    var program = avalanchePrograms.find((x) => x.value === opt.parentId);
    setFieldValue('program', program);
    const areaListFilterCriteria =
      mode !== FORM_MODE.EDIT
        ? (x) => x.parentId === program.value && !x.isDisabled
        : (x) => x.parentId === program.value;
    setAreaList(avalancheList.filter(areaListFilterCriteria).sort((a, b) => a.name.localeCompare(b.name)));
    setFieldValue('fromPoint', {}, false);
    setFieldValue('toPoint', {}, false);
    const pointListFilterCriteria =
      mode !== FORM_MODE.EDIT
        ? (x) => x.areaId === opt.value && !x.isDisabled && x.pointTypeName === 'ROAD CLOSURE POINT'
        : (x) => x.areaId === opt.value && x.pointTypeName === 'ROAD CLOSURE POINT';
    setPointList(sortedList(areaPoints.filter(pointListFilterCriteria)));
  };

  const pointOnChange = (opt, setFieldValue) => {
    const area = avalancheList.find((x) => x.value === opt.areaId);
    const areaListFilterCriteria =
      mode !== FORM_MODE.EDIT
        ? (x) => x.parentId === area.parentId && !x.isDisabled
        : (x) => x.parentId === area.parentId;
    setAreaList(avalancheList.filter(areaListFilterCriteria).sort((a, b) => a.name.localeCompare(b.name)));
    setFieldValue('area', area, false);
    const program = avalanchePrograms.find((x) => x.value === area.parentId);
    setFieldValue('program', program, false);
    const pointListFilterCriteria =
      mode !== FORM_MODE.EDIT ? (x) => x.areaId === opt.areaId && !x.isDisabled : (x) => x.areaId === opt.areaId;
    setPointList(sortedList(pointList.filter(pointListFilterCriteria)));
  };

  const breadCrumbs = () => {
    setAreaList([]);
    setArea({});
    area.value = null;
    setMode(FORM_MODE.LIST);
  };

  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);
    roadClosureAreaData.name = values.name;
    roadClosureAreaData.isDisabled = !values.active;
    roadClosureAreaData.fromPoint = values?.fromPoint;
    roadClosureAreaData.toPoint = values?.toPoint;
    roadClosureAreaData.description = values.description;
    const selectedAreaId = areaId ?? values?.area?.value;

    const roadClosureLocationDto = {
      ...roadClosureAreaData,
      AreaId: selectedAreaId,
      Name: values.name,
      IsActive: values.active ?? true,
      LocationIdFrom: values?.fromPoint?.value,
      LocationIdTo: values?.toPoint?.value,
      Description: values.description,
    };
    const area = avalancheList.find((x) => x.value === selectedAreaId);
    if (area != null) {
      setArea(area);
    }

    if (mode === FORM_MODE.EDIT) {
      updateRoadClosureAreas(setSubmitting, resetForm, setErrors, roadClosureLocationDto);
    } else {
      createRoadClosureAreas(setSubmitting, resetForm, setErrors, roadClosureLocationDto);
    }
  };

  const onClickCancel = () => {
    cancelOperation();
  };

  const onEditButtonClick = async () => {
    setMode(FORM_MODE.EDIT);
  };

  const cancelOperation = () => {
    setAreaList([]);
    setArea({});
    area.value = null;
    setMode(FORM_MODE.LIST);
  };

  const handleNavigation = (locationMode, value) => {
    const locationData = avalanchePoints.find((x) => x.value == value);
    const locationArea = area;
    navigate('/RoadClosureLocations', {
      state: { locationMode, locationData, locationArea },
    });
  };

  const redirectToAvalanchePath = (avalanchePathMode, pathData) => {
    navigate(`${Views.AvalanchePaths}/${avalanchePathMode}/${pathData.Id}`);
  };

  const columns = [
    { label: 'Path Number', key: 'PathNumber' },
    { label: 'Name', key: 'Name' },
  ];

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{
          validateOnMount: true,
          program: avalanchePrograms.find((x) => x.value === area.parentId),
          area: area ?? null,
          name: roadClosureAreaData.name ?? '',
          fromPoint: roadClosureAreaData.fromPoint ?? '',
          description: roadClosureAreaData.description ?? '',
          toPoint: roadClosureAreaData.toPoint ?? '',
          active: !roadClosureAreaData.isDisabled ?? false,
        }}
        validationSchema={Yup.object({
          program: Yup.object()
            .nullable()
            .test('program', 'Program is a required field', (value, context) => {
              return (
                (!isValueEmpty(value) && !isValueEmpty(value.value)) ||
                (context && context.originalValue && context.originalValue.length > 0)
              );
            }),
          area: Yup.object()
            .nullable()
            .test('area', 'Area is a required field', (value, context) => {
              return (
                (!isValueEmpty(value) && !isValueEmpty(value.value)) ||
                (context && context.originalValue && context.originalValue.length > 0)
              );
            }),
          name: Yup.string()
            .nullable()
            .max(50, 'Maximum 50 characters are allowed')
            .required('Road Closure Area Name is a required field'),
          description: Yup.string().nullable().max(400, 'Maximum 400 characters are allowed'),
          fromPoint: Yup.object()
            .nullable()
            .test('location', "Road Closure Area's Location From is a required field", (value, context) => {
              return (
                (!isValueEmpty(value) && !isValueEmpty(value.value)) ||
                (context && context.originalValue && context.originalValue.length > 0)
              );
            }),
          toPoint: Yup.object()
            .nullable()
            .test('location', "Road Closure Area's Location To is a required field", (value, context) => {
              return (
                (!isValueEmpty(value) && !isValueEmpty(value.value)) ||
                (context && context.originalValue && context.originalValue.length > 0)
              );
            }),
        })}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <Form>
            <span className="awp-text-btn" onClick={breadCrumbs}>
              List Road Closure Areas
            </span>
            <span className="mx-3">&gt;</span>
            <span>{title}</span>
            <Card border="light" className="page-card">
              <Card.Header>
                <Row className="mt-4"></Row>
                {crud.getHeader()}
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col sm={6}>
                    <FormSelect
                      className="avalanche-select"
                      readOnly={readOnly || mode === FORM_MODE.EDIT}
                      name="program"
                      id="program"
                      label="Program"
                      useMargin={true}
                      postChange={(e) => {
                        programOnChange(e, props.setFieldValue);
                      }}
                      options={avalanchePrograms}
                      isMulti={false}
                    />
                  </Col>
                  <Col sm={6}>
                    <FormSelect
                      className="avalanche-select"
                      readOnly={readOnly || mode === FORM_MODE.EDIT}
                      name="area"
                      id="area"
                      label="Avalanche Area"
                      useMargin={true}
                      postChange={(e) => {
                        areaOnChange(e, props.setFieldValue);
                      }}
                      options={areaList}
                      isMulti={false}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={6} className="mb-4">
                    <FormInput
                      label="Road Closure Area Name"
                      instruction="(50 Characters max)"
                      readOnly={readOnly || roadClosureAreaData.IsProtected === 'Y'}
                      name="name"
                      id="name"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={8} className="mb-4">
                    <FormTextArea
                      readOnly={readOnly}
                      label="Road Closure Area Description"
                      instruction="(400 Characters max)"
                      id="description"
                      name="description"
                      rows={3}
                    />
                  </Col>
                </Row>
                {loading && mode !== FORM_MODE.CREATE ? (
                  <Container>
                    <Spinner animation="border" />
                  </Container>
                ) : (
                  <>
                    <Row>
                      <Col sm={6} className="mb-4">
                        {(mode === FORM_MODE.EDIT && !isPointsEditable) || mode === FORM_MODE.VIEW ? (
                          <>
                            <FormLabel className="flex-grow-1">
                              <b>{'Road Closure Area From Location'}</b>
                            </FormLabel>
                            {props.values.fromPoint ? (
                              <div>
                                <span
                                  className="quick-link"
                                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                  onClick={() => handleNavigation(FORM_MODE.VIEW, props.values.fromPoint?.value)}
                                >
                                  {props.values.fromPoint?.label || 'View Details'}
                                </span>
                              </div>
                            ) : (
                              <div>
                                <span>No Location</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <FormSelect
                            className="avalanche-select"
                            readOnly={readOnly}
                            name="fromPoint"
                            id="fromPoint"
                            label="Road Closure Area From Location"
                            useMargin={true}
                            postChange={(e) => {
                              pointOnChange(e, props.setFieldValue);
                            }}
                            options={pointList.filter((x) => x.value !== props.values.toPoint?.value)}
                            isMulti={false}
                          />
                        )}
                      </Col>
                      <Col sm={6} className="mb-4">
                        {(mode === FORM_MODE.EDIT && !isPointsEditable) || mode === FORM_MODE.VIEW ? (
                          <>
                            <FormLabel className="flex-grow-1">
                              <b>{'Road Closure Area To Location'}</b>
                            </FormLabel>
                            {props.values.toPoint ? (
                              <div>
                                <span
                                  className="quick-link"
                                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                  onClick={() => handleNavigation(FORM_MODE.VIEW, props.values.toPoint?.value)}
                                >
                                  {props.values.toPoint?.label || 'View Details'}
                                </span>
                              </div>
                            ) : (
                              <div>
                                <span>No Location</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <FormSelect
                            className="avalanche-select"
                            readOnly={readOnly}
                            name="toPoint"
                            id="toPoint"
                            label="Road Closure Area To Location"
                            useMargin={true}
                            postChange={(e) => {
                              pointOnChange(e, props.setFieldValue);
                            }}
                            options={pointList.filter((x) => x.value !== props.values.fromPoint?.value)}
                            isMulti={false}
                          />
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={8} className="mb-4">
                        {pathList?.length > 0 ? (
                          <>
                            <FormListWithAction
                              label="Avalanche Path"
                              colLabel=""
                              id="avalanche-path"
                              name="avalanche-path"
                              items={pathList}
                              readOnly={false}
                              columns={columns}
                              onActionClick={(item) => {
                                redirectToAvalanchePath(FORM_MODE.VIEW, item);
                              }}
                            />
                          </>
                        ) : mode === FORM_MODE.VIEW ? (
                          <>
                            <div className="flex-grow-1">
                              <b>Avalanche Path</b>
                            </div>
                            <div className="mt-4">
                              <p>There are no related avalanche paths</p>
                            </div>
                          </>
                        ) : (
                          <></>
                        )}
                      </Col>
                    </Row>
                    {mode !== FORM_MODE.CREATE ? (
                      <>
                        {activeBtnMsg ? (
                          <div>
                            <span>{activeBtnMsg}</span>
                          </div>
                        ) : (
                          <></>
                        )}
                        <Row>
                          <Col sm={6} className="mb-4">
                            <FormCheckbox
                              useMargin={true}
                              readOnly={readOnly || (!roadClosureAreaData.isDisabled && !deactivationStatus)}
                              id="active"
                              name="active"
                              label="Active"
                              value={!roadClosureAreaData.isDisabled}
                            />
                          </Col>
                        </Row>
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </Card.Body>
              <Card.Footer>
                {!readOnly ? (
                  <AwpCloseCancelButtons
                    props={props}
                    isPristine={!props.dirty || props.isSubmitting}
                    hasAccess={true}
                    onConfirmedCancel={() => {
                      onClickCancel();
                    }}
                  />
                ) : (
                  <div className="mr-3">
                    <Button variant="outline-primary" onClick={() => onClickCancel()}>
                      {!props.dirty ? 'Close' : 'Cancel'}
                    </Button>
                    <Authorize requires={[Permissions.WriteAdminTools]}>
                      <Button className="ml-2" variant="outline-primary" onClick={onEditButtonClick}>
                        Edit Road Closure Area
                      </Button>
                    </Authorize>
                  </div>
                )}
              </Card.Footer>
            </Card>
          </Form>
        )}
      </Formik>
      <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
    </>
  );
};

export default RoadClosureAreasEdit;
